import React from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Chip } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';

const CustomerTable = ({ customers, loading, onManage, onEdit, onDelete, onRestore, onAddRuleset, showInactive, canManage }) => {
  const columns = [
    { field: 'customerName', headerName: 'Customer Name', flex: 1, minWidth: 150 },
    { field: 'customerCode', headerName: 'Customer Code', flex: 0.8, minWidth: 120 },
    { field: 'organizationDetails', headerName: 'Organization', flex: 1, minWidth: 150 },
    {
      field: 'augmentationEnabled',
      headerName: 'Augmentation',
      flex: 0.8,
      minWidth: 120,
      renderCell: (params) => (
        <Chip
          label={params.row.augmentationEnabled ? 'Enabled' : 'Disabled'}
          color={params.row.augmentationEnabled ? 'success' : 'default'}
          size="small"
          variant="outlined"
        />
      )
    },
    {
      field: 'createdDate',
      headerName: 'Created Date',
      flex: 1,
      minWidth: 120,
      valueGetter: (params) => {
        return params.row.createdDate
          ? new Date(params.row.createdDate).toLocaleDateString()
          : '';
      }
    },
    {
      field: 'isActive',
      headerName: 'Status',
      flex: 0.7,
      minWidth: 100,
      renderCell: (params) => (
        <Chip
          label={params.row.isActive ? 'Active' : 'Inactive'}
          color={params.row.isActive ? 'success' : 'default'}
          size="small"
        />
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 1,
      minWidth: 320,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" color="primary" size="small" disabled={!params.row.isActive} onClick={() => onManage(params.row.id)}>
            Manage
          </Button>
          <Button variant="outlined" color="secondary" size="small" disabled={!params.row.isActive} onClick={() => onEdit(params.row.id)}>
            Edit
          </Button>
          {canManage && params.row.isActive && (
            <Button variant="outlined" size="small" onClick={() => onAddRuleset(params.row.id)} sx={{ color: 'info.main', borderColor: 'info.main' }}>
              Add Ruleset
            </Button>
          )}
          {canManage && showInactive && !params.row.isActive ? (
            <Button variant="outlined" color="success" size="small" onClick={() => onRestore(params.row.id)}>
              Restore
            </Button>
          ) : canManage ? (
            <Button variant="outlined" color="error" size="small" onClick={() => onDelete(params.row.id)}>
              Delete
            </Button>
          ) : null}
        </Box>
      )
    }
  ];

  return (
    <Box sx={{ height: 600, width: '100%' }}>
      <DataGrid
        rows={customers}
        columns={columns}
        loading={loading}
        pageSizeOptions={[10, 25, 50, 100]}
        initialState={{
          pagination: {
            paginationModel: { pageSize: 10, page: 0 }
          }
        }}
        disableRowSelectionOnClick
        disableColumnFilter
        disableColumnSelector
        disableDensitySelector
        getRowClassName={(params) =>
          showInactive && !params.row.isActive ? 'inactive-row' : ''
        }
      />
    </Box>
  );
};

CustomerTable.propTypes = {
  customers: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      customerName: PropTypes.string.isRequired,
      customerCode: PropTypes.string,
      organizationDetails: PropTypes.string,
      augmentationEnabled: PropTypes.bool,
      createdDate: PropTypes.string,
      isActive: PropTypes.bool
    })
  ).isRequired,
  loading: PropTypes.bool,
  onManage: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onRestore: PropTypes.func.isRequired,
  onAddRuleset: PropTypes.func,
  showInactive: PropTypes.bool,
  canManage: PropTypes.bool
};

CustomerTable.defaultProps = {
  loading: false,
  showInactive: false,
  canManage: false
};

export default CustomerTable;
